// sync-findings.util.ts — the debug-mode detectors.
//
// Three things exist for every call, all on the audio clock once the report has
// rebased them: the speech that was HEARD (speaker timeline), the text that was
// WRITTEN (transcript turns, windowed onto the audio by transcript-timing.util),
// and the DECISIONS the call took in between (host marks on the session clock,
// or the interrupt.* event log for older sessions). A finding is a place where
// those three disagree. Pure and spec'd; the player only draws the result.

import { parseApiDate } from './tracking-format.util';
import { mmss, type TimedTurn, type TranscriptModel } from './transcript-timing.util';
import type { SessionReport } from './tracking-demo.models';

export type FindingKind =
  | 'untranscribed' | 'gated_onset' | 'talked_over' | 'phantom' | 'late' | 'guest_not_received';

export interface Finding {
  kind: FindingKind;
  /** Seconds on the audio clock. */
  t0: number;
  t1: number;
  who: string;
  ai: boolean;
  evidence: string[];
  /** For a talk-over: the human who was spoken over. */
  against?: string;
}

/** One host mark on the audio clock, in seconds (a span when `t1` is set). */
export interface SyncMark {
  t: number;
  t1?: number;
  kind: string;
  data?: Record<string, unknown>;
}

interface Span { name: string; at: number; end: number; }

/** Human speech shorter than this is a blip, not an utterance. */
const MIN_SPEECH_SEC = 1.0;
/** A human turn ending this long after the speech still accounts for it. */
const TRANSCRIPT_GRACE_SEC = 8;
/** The AI running on this long past a human's onset is a talk-over. */
const TALK_OVER_SEC = 1.5;
/** A turn stamped this long after its speech ended is "late", not missing. */
const LATE_SEC = 10;
const HONORED = /^interrupt\..*(_flush|_honored)$/;

export function detectFindings(model: TranscriptModel, marks: SyncMark[]): Finding[] {
  const human = spansOf(model, false);
  const ai = spansOf(model, true);
  const out: Finding[] = [];
  for (const h of human) {
    if (h.end - h.at < MIN_SPEECH_SEC) continue;
    if (!accounted(h, model.rows)) out.push(untranscribed(h, marks));
    const over = ai.find((a) => a.at < h.at && a.end > h.at + TALK_OVER_SEC);
    if (over && !marks.some((m) => HONORED.test(m.kind) && m.t >= h.at - 0.5 && m.t <= over.end)) {
      const tried = marks.filter((m) => m.kind.startsWith('interrupt.') && m.t >= h.at - 0.5 && m.t <= over.end);
      out.push({
        kind: 'talked_over', t0: h.at, t1: over.end, who: over.name, ai: true, against: h.name,
        evidence: [`${h.name} started at ${mmss(h.at)}; ${over.name} kept speaking until ${mmss(over.end)}`,
          ...tried.map((m) => `${m.kind} at ${mmss(m.t)}`)],
      });
    }
  }
  for (const r of model.rows) {
    if (r.ai || r.start == null || r.end == null) continue;
    if (!r.measured && !human.some((h) => h.at < r.end! && h.end > r.start!)) {
      out.push({ kind: 'phantom', t0: r.start, t1: r.end, who: r.name, ai: false,
        evidence: [`"${(r.turn.text || '').slice(0, 60)}" has no speech under ${mmss(r.start)}–${mmss(r.end)}`] });
    } else if (r.measured && r.tsSec != null && r.tsSec - r.end > LATE_SEC) {
      out.push({ kind: 'late', t0: r.start, t1: r.end, who: r.name, ai: false,
        evidence: [`text arrived ${mmss(r.tsSec - r.end)} after the speech ended`] });
    }
  }
  return out.sort((a, b) => a.t0 - b.t0);
}

function spansOf(model: TranscriptModel, ai: boolean): Span[] {
  return model.lanes.filter((l) => l.ai === ai)
    .flatMap((l) => l.segs.map((g) => ({ name: l.name, at: g.at, end: g.end })))
    .sort((a, b) => a.at - b.at);
}

function accounted(h: Span, rows: TimedTurn[]): boolean {
  return rows.some((r) => !r.ai && r.start != null && r.end != null
    && ((r.start < h.end && r.end > h.at) || (r.end > h.end && r.end <= h.end + TRANSCRIPT_GRACE_SEC)));
}

function untranscribed(h: Span, marks: SyncMark[]): Finding {
  const evidence = [`${h.name} spoke ${mmss(h.at)}–${mmss(h.end)}; no human turn within ${TRANSCRIPT_GRACE_SEC} s`];
  const gate = marks.find((m) => m.kind === 'gate' && m.t1 != null && m.t <= h.at && h.at < m.t1);
  const inside = marks.filter((m) => m.kind !== 'gate' && m.t >= h.at - 0.5 && m.t <= h.end + 0.5);
  for (const m of inside) evidence.push(`${m.kind} at ${mmss(m.t)}`);
  if (gate) {
    evidence.push(`echo gate closed ${mmss(gate.t)}–${mmss(gate.t1!)} (AI audible): the onset never reached the model`);
    return { kind: 'gated_onset', t0: h.at, t1: h.end, who: h.name, ai: false, evidence };
  }
  return { kind: 'untranscribed', t0: h.at, t1: h.end, who: h.name, ai: false, evidence };
}

/** Marks on the audio clock: the host's own (exact) when the recording has them,
 *  else the interrupt.* rows of the event log rebased onto the audio start. */
export function syncMarksOf(report: SessionReport): SyncMark[] {
  const rec = report.recording;
  const hostMarks = rec?.timeline?.marks;
  if (hostMarks?.length) {
    return hostMarks.map((m) => (m.t1 != null
      ? { t: m.t / 1000, t1: m.t1 / 1000, kind: m.kind, data: m.data }
      : { t: m.t / 1000, kind: m.kind, data: m.data }));
  }
  const t0 = parseApiDate(rec?.audioStartTime ?? rec?.startTime)?.getTime();
  if (t0 == null) return [];
  return (report.events || [])
    .filter((e) => e.type.startsWith('interrupt.') && e.at)
    .map((e) => ({ t: (parseApiDate(e.at)!.getTime() - t0) / 1000, kind: e.type, data: undefined }))
    .filter((m) => Number.isFinite(m.t));
}

/** Human-readable label for a finding kind. */
export function findingLabel(kind: FindingKind): string {
  switch (kind) {
    case 'untranscribed': return 'Untranscribed speech';
    case 'gated_onset': return 'Gated onset';
    case 'talked_over': return 'AI talked over';
    case 'phantom': return 'Phantom turn';
    case 'late': return 'Late transcript';
    case 'guest_not_received': return 'Guest audio not received';
  }
}
